import React from 'react';
import type { LiveSessionsMetrics } from './types';

interface KPIStripProps {
  isLoading: boolean;
  metrics: LiveSessionsMetrics | undefined;
}

interface KPI {
  hint?: string;
  label: string;
  value: number;
}

const buildKPIs = (metrics: LiveSessionsMetrics): KPI[] => [
  { label: 'Active sessions', value: metrics.active_sessions },
  { label: 'Connected students', value: metrics.connected_students },
  { label: 'Connected teachers', value: metrics.connected_teachers },
  { hint: 'In the last hour', label: 'Sessions ended', value: metrics.sessions_ended_last_hour },
];

const Card = ({ kpi }: { kpi: KPI }) => (
  <div className="tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-5 tw-shadow-sm">
    <div className="tw-text-xs tw-font-medium tw-text-gray-500 tw-uppercase tw-tracking-wide">
      {kpi.label}
    </div>
    <div className="tw-text-3xl tw-font-semibold tw-text-gray-900 tw-mt-2">
      {kpi.value.toLocaleString()}
    </div>
    {kpi.hint && (
      <div className="tw-text-xs tw-text-gray-400 tw-mt-1">{kpi.hint}</div>
    )}
  </div>
);

const SkeletonCard = () => (
  <div className="tw-bg-white tw-border tw-border-solid tw-border-gray-200 tw-rounded-lg tw-p-5 tw-shadow-sm">
    <div className="tw-h-3 tw-w-24 tw-bg-gray-200 tw-rounded tw-animate-pulse" />
    <div className="tw-h-8 tw-w-16 tw-bg-gray-100 tw-rounded tw-animate-pulse tw-mt-3" />
  </div>
);

const KPIStrip = ({ isLoading, metrics }: KPIStripProps) => {
  if (isLoading || !metrics) {
    return (
      <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
        {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}
      </div>
    );
  }

  return (
    <div className="tw-grid tw-grid-cols-2 lg:tw-grid-cols-4 tw-gap-4">
      {buildKPIs(metrics).map(kpi => <Card key={kpi.label} kpi={kpi} />)}
    </div>
  );
};

export default KPIStrip;
